import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { InvestorProfile } from '@/api/custom';
import {
  selectInvestors,
  selectInvestorsLoadingState,
} from '@/store/features/investors/investorsSlice';
import { fetchInvestorsAllThunk } from '@/store/features/investors/investorsThunk';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

type InvestorRouteState = {
  user?: InvestorProfile;
};

export const useRoutedInvestor = () => {
  const { investorId } = useParams<{ investorId: string }>();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const investors = useAppSelector(selectInvestors);
  const loadingState = useAppSelector(selectInvestorsLoadingState);
  const requestedInvestorIdRef = useRef<string | null>(null);
  const investorFromState = (location.state as InvestorRouteState | null)?.user;

  const investor = useMemo(() => {
    if (!investorId) {
      return investorFromState;
    }

    return investors.find((candidate) => candidate.id === investorId);
  }, [investorFromState, investorId, investors]);

  useEffect(() => {
    if (!investorId || investor || loadingState === 'pending') {
      return;
    }

    if (requestedInvestorIdRef.current === investorId) {
      return;
    }

    requestedInvestorIdRef.current = investorId;
    void dispatch(fetchInvestorsAllThunk());
  }, [investor, investorId, dispatch, loadingState]);

  return {
    investorId,
    investor,
    loadingState,
  };
};
